/**
 * Banner UI Module
 * Renders the consent banner, settings modal, and vendor list
 */

import { 
  purposes, 
  specialPurposes, 
  features, 
  specialFeatures, 
  dataCategories,
  getVendors,
  getVendorCount,
  getAllPurposeIds,
  getAllVendorIds,
  getAllSpecialFeatureIds,
  getVendorStorageInfo,
  getVendorRetentionInfo,
  getVendorDataCategories,
  getVendorCountForPurpose
} from './gvl-data.js';

import { 
  storeConsent, 
  storeAcceptAll, 
  storeRejectAll, 
  getStoredConsent,
  hasConsent
} from './consent-storage.js';

import { 
  setDisplayStatus, 
  notifyConsentUpdate,
  setCmpStatus
} from './tcf-api.js';

// DOM Elements
let bannerElement = null;
let settingsModal = null;
let vendorModal = null;

// Current consent state
let currentConsent = {
  purposeConsents: new Set(),
  purposeLegitimateInterests: new Set(),
  vendorConsents: new Set(),
  vendorLegitimateInterests: new Set(),
  specialFeatureOptins: new Set()
};

/**
 * Create the gear/settings icon SVG
 */
function createSettingsIcon() {
  return `<svg class="tcf-settings-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
  </svg>`;
}

/**
 * Get the Special Features being used (for first layer disclosure)
 */
function getUsedSpecialFeatures() {
  const vendors = getVendors();
  const usedSfIds = new Set();
  Object.values(vendors).forEach(vendor => {
    (vendor.specialFeatures || []).forEach(sfId => usedSfIds.add(sfId));
  });
  return Array.from(usedSfIds).map(id => specialFeatures[id]).filter(Boolean);
}

/**
 * Create the banner HTML
 * TCF 2.2 compliant text covering all Layer 1 requirements
 * Including Stacks (from GVL) and Special Features
 */
function createBannerHTML() {
  const vendorCount = getVendorCount();
  const usedSpecialFeatures = getUsedSpecialFeatures();
  const specialFeaturesText = usedSpecialFeatures.length > 0 
    ? `Some partners may use special features such as: ${usedSpecialFeatures.map(sf => sf.name.toLowerCase()).join(', ')}.` 
    : '';
  
  // TCF 2.2 Policy C(b)(IV) - Use standardised Stack names
  // Stack 42: "Personalised advertising and content, advertising and content measurement, audience research and services development"
  return `
    <div class="tcf-banner" id="tcf-banner" role="dialog" aria-modal="true" aria-labelledby="tcf-banner-title">
      <div class="tcf-banner-inner">
        <h2 class="tcf-banner-title" id="tcf-banner-title">We value your privacy</h2>
        <p class="tcf-banner-description">
          We and our <a href="#" class="tcf-vendor-link-inline" id="tcf-vendor-count-link">${vendorCount} partner${vendorCount !== 1 ? 's' : ''}</a> 
          store and access information on your device (such as cookies, device identifiers) and process personal data 
          (such as your IP address, browsing data, and unique identifiers) for the following purposes:
        </p>
        <ul class="tcf-banner-purposes">
          <li><strong>Personalised advertising and content</strong> - ads and content can be based on your profile</li>
          <li><strong>Advertising and content measurement</strong> - measure the performance of ads and content</li>
          <li><strong>Audience research and services development</strong> - understand audiences and improve services</li>
        </ul>
        ${specialFeaturesText ? `<p class="tcf-banner-description tcf-banner-special-features">${specialFeaturesText}</p>` : ''}
        <p class="tcf-banner-description tcf-banner-description-secondary">
          Your consent applies to this website only. You can withdraw your consent at any time by clicking 
          "Cookie Settings" at the bottom of any page.
        </p>
        
        <div class="tcf-banner-actions">
          <button class="tcf-btn tcf-btn-allow" id="tcf-accept-all">Allow All</button>
          <button class="tcf-btn tcf-btn-reject" id="tcf-reject-all">Reject All</button>
          <button class="tcf-settings-link" id="tcf-open-settings">
            ${createSettingsIcon()}
            Manage preferences
          </button>
        </div>
        
        <div class="tcf-banner-footer">
          <div class="tcf-branding">
            Powered by
            <span style="color: #c0392b; font-weight: bold; font-size: 14px;">CAPTAIN</span>
            Compliance
          </div>
        </div>
      </div>
    </div>
  `;
}

/**
 * Create a toggle switch HTML
 */
function createToggle(id, checked = false, disabled = false) {
  return `
    <label class="tcf-toggle">
      <input type="checkbox" id="${id}" ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}>
      <span class="tcf-toggle-slider"></span>
    </label>
  `;
}

/**
 * Generate inline vendor HTML for the Partners tab (no separate modal needed)
 * Includes all TCF 2.2 required disclosures
 */
function generateInlineVendorHTML() {
  const vendors = getVendors();
  const vendorList = Object.values(vendors);
  
  return vendorList.map(vendor => {
    // Purposes (consent-based)
    const consentPurposes = (vendor.purposes || []).map(pid => purposes[pid]?.name || `Purpose ${pid}`);
    // Legitimate Interest purposes
    const legIntPurposes = (vendor.legIntPurposes || []).map(pid => purposes[pid]?.name || `Purpose ${pid}`);
    // Special purposes (always active)
    const specialPurposesList = (vendor.specialPurposes || []).map(pid => specialPurposes[pid]?.name || `Special Purpose ${pid}`);
    // Features
    const featuresList = (vendor.features || []).map(fid => features[fid]?.name || `Feature ${fid}`);
    // Special Features
    const specialFeaturesList = (vendor.specialFeatures || []).map(fid => specialFeatures[fid]?.name || `Special Feature ${fid}`);
    
    // Storage info
    const storageInfo = getVendorStorageInfo(vendor.id);
    // Retention info  
    const retentionInfo = getVendorRetentionInfo(vendor.id);
    // Data categories
    const dataCats = getVendorDataCategories(vendor.id);
    
    // URLs
    const policyUrl = vendor.urls?.find(u => u.langId === 'en')?.privacy || '#';
    const legIntClaimUrl = vendor.urls?.find(u => u.langId === 'en')?.legIntClaim || null;
    
    return `
      <li class="tcf-vendor-item tcf-vendor-expanded">
        <div class="tcf-vendor-header">
          <div class="tcf-vendor-main">
            <div class="tcf-vendor-name">${vendor.name}</div>
            ${createToggle(`tcf-vendor-${vendor.id}`, currentConsent.vendorConsents.has(vendor.id))}
          </div>
        </div>
        
        <div class="tcf-vendor-details">
          ${consentPurposes.length ? `
            <div class="tcf-vendor-section tcf-vendor-purposes-consent">
              <div class="tcf-vendor-section-header">📋 Purposes (requires consent):</div>
              <div class="tcf-vendor-section-content">${consentPurposes.join(', ')}</div>
            </div>
          ` : ''}
          
          ${legIntPurposes.length ? `
            <div class="tcf-vendor-section tcf-vendor-legint">
              <div class="tcf-vendor-section-header">⚖️ Purposes (legitimate interest):</div>
              <div class="tcf-vendor-section-content">${legIntPurposes.join(', ')}</div>
              ${legIntClaimUrl ? `
                <div class="tcf-vendor-li-claim">
                  <a href="${legIntClaimUrl}" target="_blank" rel="noopener" class="tcf-vendor-li-claim-btn">
                    📄 View Legitimate Interest Claim
                  </a>
                </div>
              ` : ''}
            </div>
          ` : ''}
          
          ${specialPurposesList.length ? `
            <div class="tcf-vendor-section">
              <div class="tcf-vendor-section-header">🔒 Special Purposes:</div>
              <div class="tcf-vendor-section-content">${specialPurposesList.join(', ')}</div>
            </div>
          ` : ''}
          
          ${featuresList.length ? `
            <div class="tcf-vendor-section">
              <div class="tcf-vendor-section-header">⚙️ Features:</div>
              <div class="tcf-vendor-section-content">${featuresList.join(', ')}</div>
            </div>
          ` : ''}
          
          ${specialFeaturesList.length ? `
            <div class="tcf-vendor-section">
              <div class="tcf-vendor-section-header">🎯 Special Features:</div>
              <div class="tcf-vendor-section-content">${specialFeaturesList.join(', ')}</div>
            </div>
          ` : ''}
          
          ${storageInfo ? `
            <div class="tcf-vendor-section tcf-vendor-storage">
              <div class="tcf-vendor-section-header">💾 Storage Information:</div>
              <div class="tcf-vendor-section-content">
                <span class="tcf-vendor-storage-item">Max duration: <strong>${storageInfo.maxStorageDuration}</strong></span>
                <span class="tcf-vendor-storage-item">Refreshes: <strong>${storageInfo.storageRefreshes}</strong></span>
                <span class="tcf-vendor-storage-item">Uses cookies: <strong>${storageInfo.usesCookies}</strong></span>
                <span class="tcf-vendor-storage-item">Uses other storage: <strong>${storageInfo.usesNonCookieAccess}</strong></span>
              </div>
            </div>
          ` : ''}
          
          ${retentionInfo ? `
            <div class="tcf-vendor-section tcf-vendor-retention">
              <div class="tcf-vendor-section-header">📅 Data Retention Period:</div>
              <div class="tcf-vendor-section-content">
                <strong>Standard:</strong> ${retentionInfo.standardRetention}
                ${Object.keys(retentionInfo.purposeSpecific).length ? 
                  `<br><strong>Purpose-specific:</strong> ${Object.values(retentionInfo.purposeSpecific).map(p => `${p.name}: ${p.retention}`).join(', ')}` 
                  : ''}
              </div>
            </div>
          ` : ''}
          
          ${dataCats.length ? `
            <div class="tcf-vendor-section tcf-vendor-datacats">
              <div class="tcf-vendor-section-header">📊 Data Categories Collected:</div>
              <div class="tcf-vendor-section-content">${dataCats.map(c => c.name).join(', ')}</div>
            </div>
          ` : ''}
          
          <div class="tcf-vendor-links">
            <a href="${policyUrl}" class="tcf-vendor-policy-btn" target="_blank" rel="noopener">🔗 Privacy Policy</a>
          </div>
        </div>
      </li>
    `;
  }).join('');
}

/**
 * Create the settings modal HTML
 */
function createSettingsModalHTML() {
  const purposeList = Object.values(purposes);
  const specialPurposeList = Object.values(specialPurposes);
  const featureList = Object.values(features);
  const specialFeatureList = Object.values(specialFeatures);
  
  let purposesHTML = purposeList.map(purpose => {
    const hasLegInt = [2, 7, 8, 9, 10, 11].includes(purpose.id);
    const vendorCount = getVendorCountForPurpose(purpose.id);
    // TCF 2.2 Policy B(b) - Display ALL illustrations for each purpose
    const illustrations = purpose.illustrations || [];
    const illustrationHTML = illustrations.length > 0 
      ? `<div class="tcf-purpose-illustrations">
           <div class="tcf-illustration-title">Illustration(s):</div>
           ${illustrations.map(ill => `<div class="tcf-illustration-item">${ill}</div>`).join('')}
         </div>` 
      : '';
    return `
      <li class="tcf-purpose-item">
        <div class="tcf-purpose-header">
          <div class="tcf-purpose-info">
            <div class="tcf-purpose-name">${purpose.name}</div>
            <div class="tcf-purpose-vendor-count">${vendorCount} vendor${vendorCount !== 1 ? 's' : ''} use this purpose</div>
            <div class="tcf-purpose-description">${purpose.description}</div>
            ${illustrationHTML}
            ${hasLegInt ? '<div class="tcf-purpose-legal-basis">Some vendors may process this under legitimate interest. You can object by disabling this purpose.</div>' : ''}
          </div>
          ${createToggle(`tcf-purpose-${purpose.id}`, currentConsent.purposeConsents.has(purpose.id))}
        </div>
      </li>
    `;
  }).join('');
  
  let specialPurposesHTML = specialPurposeList.map(sp => {
    // TCF 2.2 Policy B(b) - Display ALL illustrations for special purposes
    const illustrations = sp.illustrations || [];
    const illustrationHTML = illustrations.length > 0 
      ? `<div class="tcf-purpose-illustrations">
           <div class="tcf-illustration-title">Illustration(s):</div>
           ${illustrations.map(ill => `<div class="tcf-illustration-item">${ill}</div>`).join('')}
         </div>` 
      : '';
    return `
      <li class="tcf-purpose-item">
        <div class="tcf-purpose-header">
          <div class="tcf-purpose-info">
            <div class="tcf-purpose-name">${sp.name}</div>
            <div class="tcf-purpose-description">${sp.description}</div>
            ${illustrationHTML}
            <div class="tcf-purpose-legal-basis">Always active (required for website operation)</div>
          </div>
          ${createToggle(`tcf-special-purpose-${sp.id}`, true, true)}
        </div>
      </li>
    `;
  }).join('');
  
  let featuresHTML = featureList.map(feature => {
    // TCF 2.2 Policy B(b) - Display ALL illustrations for features
    const illustrations = feature.illustrations || [];
    const illustrationHTML = illustrations.length > 0 
      ? `<div class="tcf-purpose-illustrations">
           <div class="tcf-illustration-title">Illustration(s):</div>
           ${illustrations.map(ill => `<div class="tcf-illustration-item">${ill}</div>`).join('')}
         </div>` 
      : '';
    return `
      <li class="tcf-purpose-item">
        <div class="tcf-purpose-header">
          <div class="tcf-purpose-info">
            <div class="tcf-purpose-name">${feature.name}</div>
            <div class="tcf-purpose-description">${feature.description}</div>
            ${illustrationHTML}
          </div>
        </div>
      </li>
    `;
  }).join('');
  
  let specialFeaturesHTML = specialFeatureList.map(sf => {
    // TCF 2.2 Policy B(b) - Display ALL illustrations for special features
    const illustrations = sf.illustrations || [];
    const illustrationHTML = illustrations.length > 0 
      ? `<div class="tcf-purpose-illustrations">
           <div class="tcf-illustration-title">Illustration(s):</div>
           ${illustrations.map(ill => `<div class="tcf-illustration-item">${ill}</div>`).join('')}
         </div>` 
      : '';
    return `
      <li class="tcf-purpose-item">
        <div class="tcf-purpose-header">
          <div class="tcf-purpose-info">
            <div class="tcf-purpose-name">${sf.name}</div>
            <div class="tcf-purpose-description">${sf.description}</div>
            ${illustrationHTML}
          </div>
          ${createToggle(`tcf-special-feature-${sf.id}`, currentConsent.specialFeatureOptins.has(sf.id))}
        </div>
      </li>
    `;
  }).join('');
  
  return `
    <div class="tcf-modal-overlay tcf-hidden" id="tcf-settings-modal">
      <div class="tcf-modal" role="dialog" aria-modal="true" aria-labelledby="tcf-settings-title">
        <div class="tcf-modal-header">
          <h3 class="tcf-modal-title" id="tcf-settings-title">Cookie Settings</h3>
          <button class="tcf-modal-close" id="tcf-settings-close" aria-label="Close">&times;</button>
        </div>
        
        <div class="tcf-modal-body">
          <div class="tcf-tabs">
            <button class="tcf-tab tcf-tab-active" data-tab="purposes">Purposes</button>
            <button class="tcf-tab" data-tab="features">Features</button>
            <button class="tcf-tab" data-tab="vendors">Partners</button>
          </div>
          
          <div class="tcf-tab-content tcf-tab-active" id="tcf-tab-purposes">
            <div class="tcf-info-box">
              We and our partners use cookies and similar technologies for the following purposes. You can choose which purposes you consent to.
            </div>
            
            <h4 class="tcf-section-header">Purposes</h4>
            <ul class="tcf-purpose-list">
              ${purposesHTML}
            </ul>
            
            <h4 class="tcf-section-header">Special Purposes</h4>
            <div class="tcf-li-info">
              <div class="tcf-li-title">These are always active</div>
              Special purposes are required for the basic operation of the website and cannot be disabled.
            </div>
            <ul class="tcf-purpose-list">
              ${specialPurposesHTML}
            </ul>
          </div>
          
          <div class="tcf-tab-content" id="tcf-tab-features">
            <div class="tcf-info-box">
              Features describe how data may be processed in support of the purposes you've consented to.
            </div>
            
            <h4 class="tcf-section-header">Features</h4>
            <ul class="tcf-purpose-list">
              ${featuresHTML}
            </ul>
            
            <h4 class="tcf-section-header">Special Features</h4>
            <div class="tcf-li-info">
              <div class="tcf-li-title">Requires your consent</div>
              Special features require explicit consent before they can be used.
            </div>
            <ul class="tcf-purpose-list">
              ${specialFeaturesHTML}
            </ul>
          </div>
          
          <div class="tcf-tab-content" id="tcf-tab-vendors">
            <div class="tcf-info-box">
              Below is the list of partners who may process your data. Review their purposes, storage practices, 
              and data retention policies. You can enable or disable each partner individually.
            </div>
            
            <div class="tcf-li-info">
              <div class="tcf-li-title">Legitimate Interest</div>
              Some partners may process your data based on legitimate interest. You have the right to object 
              to this processing by disabling the partner toggle.
            </div>
            
            <div class="tcf-storage-info">
              <div class="tcf-li-title">CMP Storage Information</div>
              This consent management platform stores your preferences for up to <strong>13 months</strong>. 
              Your choices are saved in cookies named "euconsent-v2" and "tcf_consent_data".
            </div>
            
            <ul class="tcf-vendor-list tcf-vendor-list-inline">
              ${generateInlineVendorHTML()}
            </ul>
          </div>
        </div>
        
        <div class="tcf-modal-footer">
          <button class="tcf-btn tcf-btn-outline tcf-btn-sm" id="tcf-settings-reject">Reject All</button>
          <button class="tcf-btn tcf-btn-allow tcf-btn-sm" id="tcf-settings-save">Save Preferences</button>
          <button class="tcf-btn tcf-btn-allow tcf-btn-sm" id="tcf-settings-accept">Accept All</button>
        </div>
      </div>
    </div>
  `;
}

/**
 * Create the vendor list modal HTML
 * Includes all TCF 2.2 required disclosures: storage duration, retention, data categories
 * Reuses generateInlineVendorHTML for consistency
 */
function createVendorModalHTML() {
  // Reuse the same vendor HTML for consistency
  let vendorsHTML = generateInlineVendorHTML();
  
  return `
    <div class="tcf-modal-overlay tcf-hidden" id="tcf-vendor-modal">
      <div class="tcf-modal tcf-modal-large" role="dialog" aria-modal="true" aria-labelledby="tcf-vendor-title">
        <div class="tcf-modal-header">
          <h3 class="tcf-modal-title" id="tcf-vendor-title">Our Partners</h3>
          <button class="tcf-modal-close" id="tcf-vendor-close" aria-label="Close">&times;</button>
        </div>
        
        <div class="tcf-modal-body">
          <div class="tcf-info-box">
            Below is the list of partners who may process your data. Review their purposes, storage practices, 
            and data retention policies. You can enable or disable each partner individually.
          </div>
          
          <div class="tcf-li-info">
            <div class="tcf-li-title">Legitimate Interest</div>
            Some partners may process your data based on legitimate interest. You have the right to object 
            to this processing by disabling the partner toggle.
          </div>
          
          <div class="tcf-storage-info">
            <div class="tcf-li-title">CMP Storage Information</div>
            This consent management platform stores your preferences for up to <strong>13 months</strong>. 
            Your choices are saved in cookies named "euconsent-v2" and "tcf_consent_data".
          </div>
          
          <ul class="tcf-vendor-list">
            ${vendorsHTML}
          </ul>
        </div>
        
        <div class="tcf-modal-footer">
          <button class="tcf-btn tcf-btn-outline tcf-btn-sm" id="tcf-vendor-reject-all">Reject All Vendors</button>
          <button class="tcf-btn tcf-btn-allow tcf-btn-sm" id="tcf-vendor-accept-all">Accept All Vendors</button>
          <button class="tcf-btn tcf-btn-allow tcf-btn-sm" id="tcf-vendor-save">Save & Close</button>
        </div>
      </div>
    </div>
  `;
}

/**
 * Initialize current consent state from storage or defaults
 */
function initCurrentConsent() {
  const stored = getStoredConsent();
  if (stored) {
    currentConsent = {
      purposeConsents: stored.purposeConsents || new Set(),
      purposeLegitimateInterests: stored.purposeLegitimateInterests || new Set(),
      vendorConsents: stored.vendorConsents || new Set(),
      vendorLegitimateInterests: stored.vendorLegitimateInterests || new Set(),
      specialFeatureOptins: stored.specialFeatureOptins || new Set()
    };
  }
}

/**
 * Read current toggle states from the UI
 */
function readToggleStates() {
  // Read purpose consents
  currentConsent.purposeConsents = new Set();
  currentConsent.purposeLegitimateInterests = new Set();
  getAllPurposeIds().forEach(id => {
    const toggle = document.getElementById(`tcf-purpose-${id}`);
    if (toggle && toggle.checked) {
      currentConsent.purposeConsents.add(id);
      // Add legitimate interest for purposes that support it
      if ([2, 7, 8, 9, 10, 11].includes(id)) {
        currentConsent.purposeLegitimateInterests.add(id);
      }
    }
  });
  
  // Read special feature opt-ins
  currentConsent.specialFeatureOptins = new Set();
  getAllSpecialFeatureIds().forEach(id => {
    const toggle = document.getElementById(`tcf-special-feature-${id}`);
    if (toggle && toggle.checked) {
      currentConsent.specialFeatureOptins.add(id);
    }
  });
  
  // Read vendor consents
  currentConsent.vendorConsents = new Set();
  currentConsent.vendorLegitimateInterests = new Set();
  getAllVendorIds().forEach(id => {
    const toggle = document.getElementById(`tcf-vendor-${id}`);
    if (toggle && toggle.checked) {
      currentConsent.vendorConsents.add(id);
      currentConsent.vendorLegitimateInterests.add(id);
    }
  });
}

/**
 * Update all toggles based on current consent
 */
function updateToggles() {
  // Update purpose toggles
  getAllPurposeIds().forEach(id => {
    const toggle = document.getElementById(`tcf-purpose-${id}`);
    if (toggle) {
      toggle.checked = currentConsent.purposeConsents.has(id);
    }
  });
  
  // Update special feature toggles
  getAllSpecialFeatureIds().forEach(id => {
    const toggle = document.getElementById(`tcf-special-feature-${id}`);
    if (toggle) {
      toggle.checked = currentConsent.specialFeatureOptins.has(id);
    }
  });
  
  // Update vendor toggles
  getAllVendorIds().forEach(id => {
    const toggle = document.getElementById(`tcf-vendor-${id}`);
    if (toggle) {
      toggle.checked = currentConsent.vendorConsents.has(id);
    }
  });
}

/**
 * Handle Accept All
 */
function handleAcceptAll() {
  const purposeIds = getAllPurposeIds();
  const vendorIds = getAllVendorIds();
  const specialFeatureIds = getAllSpecialFeatureIds();
  
  storeAcceptAll(purposeIds, vendorIds, specialFeatureIds);
  notifyConsentUpdate();
  hideBanner();
  hideSettingsModal();
}

/**
 * Handle Reject All
 */
function handleRejectAll() {
  storeRejectAll();
  notifyConsentUpdate();
  hideBanner();
  hideSettingsModal();
}

/**
 * Handle Save Preferences
 */
function handleSavePreferences() {
  readToggleStates();
  storeConsent(currentConsent);
  notifyConsentUpdate();
  hideBanner();
  hideSettingsModal();
}

/**
 * Show the banner
 */
export function showBanner() {
  if (bannerElement) {
    bannerElement.classList.remove('tcf-hidden');
    setDisplayStatus('visible');
  }
}

/**
 * Hide the banner
 */
export function hideBanner() {
  if (bannerElement) {
    bannerElement.classList.add('tcf-hidden');
    setDisplayStatus('hidden');
  }
}

/**
 * Show settings modal
 */
function showSettingsModal() {
  initCurrentConsent();
  
  // Re-render modals with current consent
  if (settingsModal) {
    settingsModal.remove();
  }
  if (vendorModal) {
    vendorModal.remove();
  }
  
  document.body.insertAdjacentHTML('beforeend', createSettingsModalHTML());
  document.body.insertAdjacentHTML('beforeend', createVendorModalHTML());
  
  settingsModal = document.getElementById('tcf-settings-modal');
  vendorModal = document.getElementById('tcf-vendor-modal');
  
  attachModalEventListeners();
  
  settingsModal.classList.remove('tcf-hidden');
}

/**
 * Hide settings modal
 */
function hideSettingsModal() {
  if (settingsModal) {
    settingsModal.classList.add('tcf-hidden');
  }
}

/**
 * Show vendor modal
 */
function showVendorModal() {
  if (vendorModal) {
    vendorModal.classList.remove('tcf-hidden');
  }
}

/**
 * Hide vendor modal
 */
function hideVendorModal() {
  if (vendorModal) {
    vendorModal.classList.add('tcf-hidden');
  }
}

/**
 * Handle tab switching
 */
function handleTabSwitch(e) {
  const tabName = e.target.dataset.tab;
  if (!tabName) return;
  
  // Update tab buttons
  document.querySelectorAll('.tcf-tab').forEach(tab => {
    tab.classList.remove('tcf-tab-active');
  });
  e.target.classList.add('tcf-tab-active');
  
  // Update tab content
  document.querySelectorAll('.tcf-tab-content').forEach(content => {
    content.classList.remove('tcf-tab-active');
  });
  document.getElementById(`tcf-tab-${tabName}`).classList.add('tcf-tab-active');
}

/**
 * Attach event listeners to the banner
 */
function attachBannerEventListeners() {
  // Accept All button
  document.getElementById('tcf-accept-all')?.addEventListener('click', handleAcceptAll);
  
  // Reject All button
  document.getElementById('tcf-reject-all')?.addEventListener('click', handleRejectAll);
  
  // Open Settings button
  document.getElementById('tcf-open-settings')?.addEventListener('click', (e) => {
    e.preventDefault();
    showSettingsModal();
  });
  
  // Vendor count link - opens settings on Partners tab
  document.getElementById('tcf-vendor-count-link')?.addEventListener('click', (e) => {
    e.preventDefault();
    showSettingsModal();
    // Switch to vendors tab after modal renders
    setTimeout(() => {
      const vendorTab = document.querySelector('[data-tab="vendors"]');
      if (vendorTab) vendorTab.click();
    }, 50);
  });
}

/**
 * Attach event listeners to the footer link
 */
function attachFooterEventListeners() {
  document.getElementById('tcf-footer-settings-btn')?.addEventListener('click', () => {
    showSettingsModal();
  });
}

/**
 * Attach event listeners to modals
 */
function attachModalEventListeners() {
  // Settings modal close
  document.getElementById('tcf-settings-close')?.addEventListener('click', hideSettingsModal);
  
  // Settings modal buttons
  document.getElementById('tcf-settings-reject')?.addEventListener('click', handleRejectAll);
  document.getElementById('tcf-settings-save')?.addEventListener('click', handleSavePreferences);
  document.getElementById('tcf-settings-accept')?.addEventListener('click', handleAcceptAll);
  
  // Tab switching
  document.querySelectorAll('.tcf-tab').forEach(tab => {
    tab.addEventListener('click', handleTabSwitch);
  });
  
  // Open vendors
  document.getElementById('tcf-open-vendors')?.addEventListener('click', showVendorModal);
  
  // Vendor modal close
  document.getElementById('tcf-vendor-close')?.addEventListener('click', hideVendorModal);
  
  // Vendor modal buttons
  document.getElementById('tcf-vendor-reject-all')?.addEventListener('click', () => {
    getAllVendorIds().forEach(id => {
      const toggle = document.getElementById(`tcf-vendor-${id}`);
      if (toggle) toggle.checked = false;
    });
  });
  
  document.getElementById('tcf-vendor-accept-all')?.addEventListener('click', () => {
    getAllVendorIds().forEach(id => {
      const toggle = document.getElementById(`tcf-vendor-${id}`);
      if (toggle) toggle.checked = true;
    });
  });
  
  document.getElementById('tcf-vendor-save')?.addEventListener('click', () => {
    readToggleStates();
    hideVendorModal();
  });
  
  // Close modals on overlay click
  settingsModal?.addEventListener('click', (e) => {
    if (e.target === settingsModal) {
      hideSettingsModal();
    }
  });
  
  vendorModal?.addEventListener('click', (e) => {
    if (e.target === vendorModal) {
      hideVendorModal();
    }
  });
}

/**
 * Create the persistent footer link HTML
 * This allows users to resurface the consent UI at any time
 */
function createFooterLinkHTML() {
  return `
    <div class="tcf-footer-link" id="tcf-footer-link">
      <button class="tcf-footer-btn" id="tcf-footer-settings-btn" aria-label="Cookie Settings">
        ${createSettingsIcon()}
        <span>Cookie Settings</span>
      </button>
    </div>
  `;
}

/**
 * Initialize the banner UI
 */
export function initBannerUI() {
  // Initialize consent state
  initCurrentConsent();
  
  // Create and insert banner
  document.body.insertAdjacentHTML('beforeend', createBannerHTML());
  bannerElement = document.getElementById('tcf-banner');
  
  // Create and insert persistent footer link
  document.body.insertAdjacentHTML('beforeend', createFooterLinkHTML());
  
  // Attach event listeners
  attachBannerEventListeners();
  attachFooterEventListeners();
  
  // Set CMP as ready
  setCmpStatus('ready');
  
  // Show banner if no consent exists
  if (!hasConsent()) {
    showBanner();
  } else {
    hideBanner();
    // Notify that consent already exists
    notifyConsentUpdate();
  }
  
  console.log('TCF Banner UI initialized');
}

/**
 * Destroy the banner UI (for cleanup)
 */
export function destroyBannerUI() {
  bannerElement?.remove();
  settingsModal?.remove();
  vendorModal?.remove();
  bannerElement = null;
  settingsModal = null;
  vendorModal = null;
}

/**
 * Programmatically open the settings modal (for "manage preferences" links)
 */
export function openSettings() {
  showSettingsModal();
}



